import React, { useState } from 'react';
import { 
  Table, 
  Card, 
  Input, 
  Select, 
  Button, 
  Space, 
  Tag, 
  Typography, 
  Row, 
  Col,
  DatePicker,
  Statistic,
  message
} from 'antd';
import { 
  SearchOutlined, 
  ReloadOutlined, 
  EyeOutlined,
  UserOutlined,
  CrownOutlined
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { AdminReportsUser, adminApi } from '../lib/api';

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const Users: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [subscriptionFilter, setSubscriptionFilter] = useState<string>('');
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [sortOrder, setSortOrder] = useState('total_desc');

  const {
    data: usersData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['users', pagination.current, pagination.pageSize, sortOrder, dateRange],
    queryFn: async () => {
      const params: any = {
        limit: pagination.pageSize,
        offset: (pagination.current - 1) * pagination.pageSize,
        sort: sortOrder,
      };

      if (dateRange) {
        params.start = dateRange[0];
        params.end = dateRange[1];
      }

      const result = await adminApi.getUsers(params);
      
      setPagination(prev => ({
        ...prev,
        total: result.total_count || result.total_users || 0
      }));

      return result;
    },
    retry: 1,
    staleTime: 30000, // 30 seconds
  });

  const getTierColor = (tier: string) => {
    const normalizedTier = tier?.toLowerCase();
    const colors: Record<string, string> = {
      'free': 'default',
      'bronze': '#CD7F32',
      'silver': '#C0C0C0', 
      'gold': '#FFD700',
      'firooze': '#40E0D0',
      'firoozeh': '#40E0D0' // Support both spellings
    };
    return colors[normalizedTier] || 'default';
  };

  const getTierDisplayName = (tier: string) => {
    const normalizedTier = tier?.toLowerCase();
    const names: Record<string, string> = {
      'free': 'Free',
      'bronze': 'Bronze',
      'silver': 'Silver',
      'gold': 'Gold',
      'firooze': 'Firooze',
      'firoozeh': 'Firooze' // Support both spellings
    };
    return names[normalizedTier] || tier;
  };

  const filteredUsers = usersData?.users?.filter((user: AdminReportsUser) => {
    const searchableText = [
      user.username || user.user_id, // Use user_id if username not available
      user.email,
      user.full_name
    ].filter(Boolean).join(' ').toLowerCase();
    
    const matchesSearch = !searchTerm || searchableText.includes(searchTerm.toLowerCase());
    const tierToCheck = user.tier || user.subscription_tier; // Handle both field names
    const matchesSubscription = !subscriptionFilter || tierToCheck === subscriptionFilter;
    
    return matchesSearch && matchesSubscription;
  }) || [];

  const columns = [
    {
      title: 'User',
      key: 'user',
      render: (user: AdminReportsUser) => (
        <Space>
          <UserOutlined />
          <div>
            <div style={{ fontWeight: 500 }}>{user.username || user.user_id}</div>
            {user.full_name && <div style={{ fontSize: '12px', color: '#666' }}>{user.full_name}</div>}
            {user.email && <div style={{ fontSize: '12px', color: '#666' }}>{user.email}</div>}
            {user.telegram_id && <div style={{ fontSize: '12px', color: '#666' }}>TG: {user.telegram_id}</div>}
          </div>
        </Space>
      ),
      width: 250,
    },
    {
      title: 'Subscription',
      dataIndex: 'tier',
      key: 'tier',
      render: (tier: string, record: AdminReportsUser) => {
        const userTier = tier || record.subscription_tier; // Handle both field names
        return (
          <Tag 
            color={getTierColor(userTier)} 
            icon={userTier !== 'free' ? <CrownOutlined /> : undefined}
          >
            {getTierDisplayName(userTier)}
          </Tag>
        );
      },
      width: 120,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
      width: 100,
    },
    {
      title: 'Usage Stats',
      key: 'usage',
      render: (user: AdminReportsUser) => (
        <Space direction="vertical" size="small">
          <div style={{ fontSize: '12px' }}>
            Try-ons: <strong>{user.try_on_total || 0}</strong>
          </div>
          <div style={{ fontSize: '12px' }}>
            Smart Add: <strong>{user.smart_add_total || 0}</strong>
          </div>
          <div style={{ fontSize: '12px' }}>
            Days Active: <strong>{user.days || 0}</strong>
          </div>
        </Space>
      ),
      width: 120,
    },
    {
      title: 'Total Usage',
      dataIndex: 'total',
      key: 'total',
      render: (total: number) => (
        <div style={{ fontWeight: 500, color: total > 0 ? '#1890ff' : '#999' }}>
          {total || 0}
        </div>
      ),
      width: 100,
      sorter: true,
    },
    {
      title: 'Last Activity',
      dataIndex: 'last_date',
      key: 'last_date',
      render: (date: string) => {
        if (!date) return 'Never';
        try {
          return format(new Date(date), 'MMM dd, yyyy');
        } catch {
          return date; // Return the original string if date parsing fails
        }
      },
      width: 120,
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => date ? format(new Date(date), 'MMM dd, yyyy') : 'Unknown',
      width: 120,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (user: AdminReportsUser) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/users/${user.user_id}`)}
          >
            View Details
          </Button>
        </Space>
      ),
      width: 130,
    },
  ];

  const handleTableChange = (paginationInfo: any, filters: any, sorter: any) => {
    setPagination({
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
      total: pagination.total,
    });

    if (sorter.field) {
      const order = sorter.order === 'ascend' ? 'asc' : 'desc';
      setSortOrder(`${sorter.field}_${order}`);
    }
  };

  const handleRefresh = () => {
    refetch();
    message.success('Users data refreshed');
  };

  const totalUsers = usersData?.total_count || usersData?.total_users || 0;
  const activeUsers = filteredUsers?.filter(user => user.is_active).length || 0;
  const paidUsers = filteredUsers?.filter(user => {
    const tier = user.tier || user.subscription_tier;
    return tier && tier !== 'free';
  }).length || 0;

  // Error handling
  if (error) {
    return (
      <div className="page-content">
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Title level={2}>Users Management</Title>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <div style={{ 
              padding: '48px',
              textAlign: 'center',
              background: '#fff',
              borderRadius: '8px',
              border: '1px solid #f0f0f0'
            }}>
              <h3>Error Loading Users</h3>
              <p>Failed to load user data. Please check your connection and try again.</p>
              <Button type="primary" onClick={() => refetch()}>Retry</Button>
            </div>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col span={24}>
          <Title level={2}>Users Management</Title>
        </Col>
      </Row>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Total Users"
              value={totalUsers}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Active Users"
              value={activeUsers}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Paid Subscribers"
              value={paidUsers}
              prefix={<CrownOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: '16px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={8}>
            <Input
              placeholder="Search users..."
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={6}>
            <Select
              placeholder="Filter by subscription"
              value={subscriptionFilter}
              onChange={setSubscriptionFilter}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="free">Free</Option>
              <Option value="bronze">Bronze</Option>
              <Option value="silver">Silver</Option>
              <Option value="gold">Gold</Option>
              <Option value="firooze">Firooze</Option>
              <Option value="firoozeh">Firooze</Option>
            </Select>
          </Col>
          <Col xs={24} sm={6}>
            <RangePicker
              placeholder={['Start date', 'End date']}
              onChange={(dates) => {
                if (dates) {
                  setDateRange([
                    dates[0]!.format('YYYY-MM-DD'),
                    dates[1]!.format('YYYY-MM-DD')
                  ]);
                } else {
                  setDateRange(null);
                }
              }}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={4}>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleRefresh}
                loading={isLoading}
              >
                Refresh
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Users Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredUsers}
          rowKey="user_id"
          loading={isLoading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
        />
      </Card>
    </div>
  );
};

export default Users;